import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Button } from '../ui/button';
import {
  LayoutDashboard,
  Settings,
  Youtube,
  Video,
  FileText,
  Languages,
  Users,
  MessageSquare,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Sun,
  Moon,
  Home,
  Image
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const { user, logout, theme, toggleTheme } = useApp();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'editor', 'viewer'] },
    { path: '/admin/settings', icon: Settings, label: 'Settings', roles: ['admin'] },
    { path: '/admin/youtube', icon: Youtube, label: 'YouTube', roles: ['admin', 'editor'] },
    { path: '/admin/videos', icon: Video, label: 'Videos', roles: ['admin', 'editor', 'viewer'] },
    { path: '/admin/media', icon: Image, label: 'Media', roles: ['admin', 'editor'] },
    { path: '/admin/content', icon: FileText, label: 'Content', roles: ['admin', 'editor'] },
    { path: '/admin/translations', icon: Languages, label: 'Translations', roles: ['admin', 'editor'] },
    { path: '/admin/messages', icon: MessageSquare, label: 'Messages', roles: ['admin', 'editor', 'viewer'] },
    { path: '/admin/users', icon: Users, label: 'Users', roles: ['admin'] }
  ];

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(user?.role || 'viewer')
  );

  const Sidebar = ({ mobile = false }) => (
    <div className={`
      ${mobile ? 'w-full' : sidebarOpen ? 'w-64' : 'w-20'} 
      bg-brand-dark border-r border-white/5 h-full flex flex-col transition-all duration-300
    `}>
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-white/5">
        {(sidebarOpen || mobile) && (
          <span className="font-syne font-bold text-white">ADMIN</span>
        )}
        {!mobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white/70 hover:text-white"
            data-testid="sidebar-toggle"
          >
            <ChevronRight className={`w-5 h-5 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {filteredMenuItems.map(item => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
            (item.path !== '/admin' && location.pathname.startsWith(item.path));
          
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              className={`
                flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                ${isActive 
                  ? 'bg-brand-red text-white' 
                  : 'text-white/60 hover:text-white hover:bg-white/5'
                }
              `}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {(sidebarOpen || mobile) && (
                <span className="font-manrope text-sm">{item.label}</span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User & Logout */}
      <div className="p-4 border-t border-white/5">
        {(sidebarOpen || mobile) && (
          <div className="mb-3 px-3">
            <p className="text-white text-sm font-medium truncate">{user?.username}</p>
            <p className="text-white/40 text-xs capitalize">{user?.role}</p>
          </div>
        )}
        <div className="flex gap-2">
          <Link to="/" className="flex-1">
            <Button
              variant="outline"
              size="sm"
              className="w-full border-white/10 text-white/70 hover:text-white hover:bg-white/5"
              data-testid="admin-view-site"
            >
              <Home className="w-4 h-4" />
              {(sidebarOpen || mobile) && <span className="ml-2">Site</span>}
            </Button>
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="border-white/10 text-white/70 hover:text-brand-red hover:border-brand-red"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-void flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:block fixed left-0 top-0 h-full z-40">
        <Sidebar />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/80 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={`
        md:hidden fixed left-0 top-0 h-full z-50 transform transition-transform duration-300
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <Sidebar mobile />
      </aside>

      {/* Main Content */}
      <div className={`flex-1 ${sidebarOpen ? 'md:ml-64' : 'md:ml-20'} transition-all duration-300`}>
        {/* Top Bar */}
        <header className="sticky top-0 z-30 h-16 bg-brand-dark/80 backdrop-blur-xl border-b border-white/5 flex items-center justify-between px-4 md:px-8">
          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-white/70 hover:text-white"
            data-testid="mobile-admin-menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>

          {/* Page Title */}
          <h1 className="font-syne font-semibold text-white text-lg md:text-xl">
            {filteredMenuItems.find(item => 
              location.pathname === item.path || 
              (item.path !== '/admin' && location.pathname.startsWith(item.path))
            )?.label || 'Admin'}
          </h1>

          {/* Right Controls */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="text-white/70 hover:text-white"
            data-testid="admin-theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
